import { useState } from 'react';
import { apiUploadDocument } from 'src/api/DocumentServiceApi';
import { MAX_FILE_SIZE_BYTES, SERVER_UPLOAD_ERROR_TYPES, VALID_FILENAME_REGEX, supportedFiles, } from './UploadConstants';
const isServerUploadErrorType = (value) => typeof value === 'string' && SERVER_UPLOAD_ERROR_TYPES.includes(value);
const getServerUploadErrorType = (error) => {
    const errorType = error?.response?.data
        ?.errorType;
    return isServerUploadErrorType(errorType) ? errorType : undefined;
};
/**
 * Resolves the validation copy for a server-side upload error code from the flat message fields
 * on the language file. INVALID_FILENAME, EMPTY_FILE, UNSUPPORTED_EXTENSION and FILE_TOO_LARGE
 * reuse the existing client-side messages (uploadFilenameInvalid / uploadFileEmpty /
 * uploadFileTypeInvalid / uploadSizeExceeded) since the meaning is identical.
 */
const getServerUploadErrorMessage = (languageFile, errorType) => {
    switch (errorType) {
        case 'INVALID_FILENAME':
            return languageFile.uploadFilenameInvalid;
        case 'EMPTY_FILE':
            return languageFile.uploadFileEmpty;
        case 'UNSUPPORTED_EXTENSION':
            return languageFile.uploadFileTypeInvalid;
        case 'CORRUPT_FILE':
            return languageFile.uploadFileCorrupted;
        case 'PASSWORD_PROTECTED':
            return languageFile.uploadPasswordProtected;
        case 'FILE_TOO_LARGE':
            return languageFile.uploadSizeExceeded;
        default:
            return languageFile.apiFailureMessage;
    }
};
/**
 * Owns the error/loading state for a document upload feature: client-side file
 * validation, the upload try/catch, and a uniform two-error model
 * (validation `errorMessage` + `apiFailureMessage`).
 *
 * It deliberately does NOT own the uploaded-document list — each feature stores
 * that differently (CLI context, View Status local state + submit step, RTW via
 * props) — nor does it fire analytics itself; callers supply that via the
 * onUploadSuccess / onValidationError / onApiError hooks.
 */
const useDocumentUpload = ({ languageFile, interviewType, experience, authHeader, onUploadSuccess, onValidationError, onApiError, }) => {
    const [errorMessage, setErrorMessage] = useState('');
    const [apiFailureMessage, setApiFailureMessage] = useState(false);
    const [uploading, setUploading] = useState(false);
    const clearStates = () => {
        setErrorMessage('');
        setApiFailureMessage(false);
        setUploading(false);
    };
    const getFormData = (file) => {
        const formData = new FormData();
        formData.append('file', file, file.name);
        formData.append('interviewType', interviewType);
        formData.append('experience', experience);
        return formData;
    };
    const failValidation = (type, message) => {
        setErrorMessage(message);
        onValidationError?.(type, message);
    };
    const uploadDocument = async (file) => {
        try {
            setUploading(true);
            const response = await apiUploadDocument(getFormData(file), authHeader);
            setErrorMessage('');
            setApiFailureMessage(false);
            setUploading(false);
            onUploadSuccess(response, file);
        }
        catch (error) {
            setUploading(false);
            const serverErrorType = getServerUploadErrorType(error);
            if (serverErrorType) {
                failValidation(serverErrorType, getServerUploadErrorMessage(languageFile, serverErrorType));
            }
            else {
                setApiFailureMessage(true);
            }
            onApiError?.(error);
        }
    };
    const onUpload = async (event) => {
        // Clear any prior errors up front so a stale alert doesn't linger past a re-selection.
        setErrorMessage('');
        setApiFailureMessage(false);
        const { files } = event.target;
        if (!files || files.length < 1) {
            return;
        }
        const selectedFile = files[0];
        const fileName = selectedFile.name;
        if (!fileName.match(VALID_FILENAME_REGEX)) {
            failValidation('filename', languageFile.uploadFilenameInvalid);
            return;
        }
        const fileType = fileName.substring(fileName.lastIndexOf('.') + 1, fileName.length);
        if (supportedFiles.indexOf(fileType.toLocaleLowerCase()) < 0) {
            failValidation('fileType', languageFile.uploadFileTypeInvalid);
            return;
        }
        if (selectedFile.size === 0) {
            failValidation('empty', languageFile.uploadFileEmpty);
            return;
        }
        if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
            failValidation('sizeExceeded', languageFile.uploadSizeExceeded);
            return;
        }
        await uploadDocument(selectedFile);
        // clear the input so the same file can be re-selected
        event.target.value = '';
    };
    return {
        errorMessage,
        apiFailureMessage,
        uploading,
        onUpload,
        clearStates,
        setApiFailureMessage,
    };
};
export default useDocumentUpload;
